import {
  TwinConversation,
  TwinConversationGroup,
  TwinConversationTag,
  TwinsConversationsReviewRequests,
} from 'api/model'
import { useActiveOrganization } from './use-organization'
import { useCallback, useLayoutEffect, useState } from 'react'
import {
  twinsConversation,
  twinsConversationByTag,
  twinsConversations,
  twinsGetConversationCounts,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { useRecoilState } from 'recoil'
import {
  conversationsGroupedByTwinAtom,
  conversationsWithTwinAtom,
  recentConversationsAtom,
  conversationsCountAtom,
} from 'state/conversations'
import { internalConversationAtom } from 'state/internal-conversation'

// fetches a specific conversation
function useFetchTwinConversation(conversationId: string, twinId: string) {
  const [activeOrganization] = useActiveOrganization()
  const [conversation, setConversation] = useState<TwinConversation | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!conversationId) {
        setConversation(null)
        return
      }

      if (!activeOrganization || !twinId) {
        return
      }

      setIsLoading(true)

      try {
        const response = await twinsConversation(activeOrganization, conversationId, {
          twin_id: twinId,
        })

        setConversation(response.data ? response.data : null)
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [conversationId, twinId, activeOrganization])

  return {
    isLoading,
    error,
    conversation,
    setConversation,
  }
}

function useFetchAnonymousRecentConversations(twinOrgId: string, twinId: string) {
  const [recentConversations, setRecentConversations] = useRecoilState<TwinConversationGroup[]>(recentConversationsAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!twinOrgId || !twinId) {
        return
      }

      setIsLoading(true)

      try {
        const response = await twinsConversations(twinOrgId, {
          groupby_twin: false,
          twin_id: twinId,
        })

        setRecentConversations(response.data ? response.data : [])
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [twinOrgId, setRecentConversations, twinId])

  return {
    isLoading,
    recentConversations,
    setRecentConversations,
    error,
  }
}

// used in the case of fetching the conversation between the user and their twin via the chat bot experience
function useFetchInternalConversation() {
  const [activeOrganization] = useActiveOrganization()
  const [internalConversation, setInternalConversation] = useRecoilState<TwinConversation | null>(
    internalConversationAtom
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!activeOrganization) {
        return
      }

      setIsLoading(true)

      try {
        const response = await twinsConversationByTag(activeOrganization, {
          tag: TwinConversationTag.internal,
        })

        if (response.data) {
          setInternalConversation(response.data)
        }
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    // we only want to fetch if we don't already have the internal conversation
    if (!internalConversation) {
      fetchData()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrganization, setInternalConversation])

  return {
    isLoading,
    internalConversation,
    error,
    setInternalConversation,
  }
}

type RecentConversationsOptions = {
  shouldFetchOnMount: boolean
}

function useFetchRecentConversations(options: RecentConversationsOptions) {
  const { shouldFetchOnMount } = options
  const [activeOrganization] = useActiveOrganization()
  const [recentConversations, setRecentConversations] = useRecoilState<TwinConversationGroup[]>(recentConversationsAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetchRecentConversations = useCallback(
    async (updatedSince?: string) => {
      if (!activeOrganization) {
        return
      }

      setIsLoading(true)

      try {
        const response = await twinsConversations(activeOrganization, {
          groupby_twin: false,
          updated_since: updatedSince || undefined,
        })

        // if updatedSince exists, then we should only update the conversations that are coming back from the API response rather than setting all of them
        if (updatedSince) {
          setRecentConversations((prevRecentConversations) => {
            const updatedConversations = response.data ? response.data : []
            const updatedRecentConversations = prevRecentConversations.map((conversation) => {
              const updatedConversation = updatedConversations.find((updatedConversation) => {
                return updatedConversation.twin_conversations[0].id === conversation.twin_conversations[0].id
              })

              if (updatedConversation) {
                return updatedConversation
              }

              return conversation
            })

            return updatedRecentConversations
          })
        } else {
          // otherwise, if updated since doesn't exist, then we should just set the conversations that come back
          // TODO: fix this, we shouldn't have void coming back from twinsConversations response
          setRecentConversations(response.data ? response.data : [])
        }
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [activeOrganization, setRecentConversations]
  )

  useLayoutEffect(() => {
    if (shouldFetchOnMount) {
      refetchRecentConversations()
    }
  }, [refetchRecentConversations, shouldFetchOnMount])

  return {
    isLoading,
    recentConversations,
    error,
    setRecentConversations,
    refetchRecentConversations,
  }
}

type Options = {
  groupByTwin?: boolean
  shouldFetchOnMount?: boolean
}

function useFetchConversationsGroupedByTwin(options: Options) {
  const [activeOrganization] = useActiveOrganization()
  const [conversationsGroupedByTwin, setConversationsGroupedByTwin] =
    useRecoilState<TwinConversationGroup[]>(conversationsGroupedByTwinAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetchConversationsGroupedByTwin = useCallback(
    async (updatedSince?: string) => {
      if (!activeOrganization) {
        return
      }

      setIsLoading(true)

      try {
        const response = await twinsConversations(activeOrganization, {
          groupby_twin: options.groupByTwin,
          updated_since: updatedSince || undefined,
        })
        // if updatedSince exists, then we should only update the conversations that are coming back from the API response rather than setting all of them
        if (updatedSince) {
          setConversationsGroupedByTwin((prevConversationsGroupedByTwin) => {
            // this is the list of conversations that need to be updated
            const conversationsToUpdate = response.data ? response.data : []
            const flatConversations = conversationsToUpdate.flatMap((conversation) => {
              return conversation.twin_conversations
            })

            const updatedConversationsGroupedByTwin = prevConversationsGroupedByTwin.map((conversationGroup) => {
              // for each group, first see if there is a conversation that needs to be updated
              const twinConversations = conversationGroup.twin_conversations.map((conversation) => {
                const updatedConversation = flatConversations.find((updatedConversation) => {
                  return updatedConversation.id === conversation.id
                })

                if (updatedConversation) {
                  return updatedConversation
                }

                return conversation
              })

              return {
                twin: conversationGroup.twin,
                twin_conversations: twinConversations,
              }
            })

            return updatedConversationsGroupedByTwin
          })
        } else {
          // otherwise, if updated since doesn't exist, then we should just set the conversations that come back
          setConversationsGroupedByTwin(response.data ? response.data : [])
        }
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    },
    [activeOrganization, setConversationsGroupedByTwin, options.groupByTwin]
  )

  useLayoutEffect(() => {
    if (options.shouldFetchOnMount) {
      refetchConversationsGroupedByTwin()
    }
  }, [refetchConversationsGroupedByTwin, options.shouldFetchOnMount])

  return {
    isLoading,
    conversationsGroupedByTwin,
    error,
    setConversationsGroupedByTwin,
    refetchConversationsGroupedByTwin,
  }
}

function useFetchConversationsWithTwin(twinId: string, reviewRequests?: TwinsConversationsReviewRequests) {
  const [activeOrganization] = useActiveOrganization()
  const [conversationsWithTwin, setConversationsWithTwin] =
    useRecoilState<TwinConversationGroup[]>(conversationsWithTwinAtom)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useLayoutEffect(() => {
    async function fetchData() {
      if (!activeOrganization || !twinId) {
        return
      }

      setIsLoading(true)

      try {
        const response = await twinsConversations(activeOrganization, {
          twin_id: twinId,
          review_requests: reviewRequests,
        })
        setConversationsWithTwin(response.data ? response.data : [])
      } catch (error) {
        setError(`${error}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [activeOrganization, setConversationsWithTwin, twinId, reviewRequests])

  return {
    isLoading,
    conversationsWithTwin,
    error,
    setConversationsWithTwin,
  }
}

function useFetchConversationCounts() {
  const [activeOrganization] = useActiveOrganization()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [conversationCounts, setConversationCounts] = useRecoilState<number>(conversationsCountAtom)

  const refetchConversationCounts = useCallback(async () => {
    if (!activeOrganization) {
      return
    }

    setIsLoading(true)

    try {
      const response = await twinsGetConversationCounts(activeOrganization)
      setConversationCounts(response.data ? response.data.review_count : 0)
    } catch (error) {
      setError(`${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, setConversationCounts])

  useLayoutEffect(() => {
    if (activeOrganization) {
      refetchConversationCounts()
    }
  }, [activeOrganization, setConversationCounts, refetchConversationCounts])

  return {
    isLoading,
    conversationCounts,
    error,
    setConversationCounts,
    refetchConversationCounts,
  }
}

export {
  useFetchConversationsGroupedByTwin,
  useFetchRecentConversations,
  useFetchAnonymousRecentConversations,
  useFetchConversationsWithTwin,
  useFetchInternalConversation,
  useFetchTwinConversation,
  useFetchConversationCounts,
}
